"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HorizontalScroll } from "@/components/ui/HorizontalScroll";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useNavigation } from "@/lib/navigation/NavigationProvider";

interface Team {
    id: string;
    fansUnitedTeamId?: string;
    name: string;
    logo?: string;
    uuid?: string;
    primaryColor?: string;
    secondaryColor?: string;
    countryCode?: string;
    countryFlag?: string;
}

export interface TeamLogosScrollProps {
    teams: Team[];
    /** "logos" = compact logo row (default), "cards" = large cards with name */
    variant?: "logos" | "cards";
    /** Header element rendered inline with scroll controls (cards variant only) */
    header?: React.ReactNode;
}

/** Cards variant with scroll progress bar and arrow controls */
function TeamCardsScroll({
    teams,
    nav,
    header,
}: {
    teams: Team[];
    nav: ReturnType<typeof useNavigation>;
    header?: React.ReactNode;
}) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) {
            setScrollProgress(1);
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }
        setScrollProgress(el.scrollLeft / maxScroll);
        setCanScrollLeft(el.scrollLeft > 1);
        setCanScrollRight(el.scrollLeft < maxScroll - 1);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScroll();
        el.addEventListener("scroll", updateScroll, { passive: true });
        window.addEventListener("resize", updateScroll);
        return () => {
            el.removeEventListener("scroll", updateScroll);
            window.removeEventListener("resize", updateScroll);
        };
    }, [updateScroll]);

    const scroll = useCallback((direction: "left" | "right") => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: (direction === "left" ? -1 : 1) * el.clientWidth * 0.8, behavior: "smooth" });
    }, []);

    return (
        <div>
            {/* Header + progress bar + arrows */}
            <div className="flex items-center gap-2 mb-3">
                {header && <div className="flex-1 min-w-0">{header}</div>}
                <div className="w-24 md:w-32 h-1 rounded-full bg-card overflow-hidden shrink-0">
                    <div
                        className="h-full rounded-full bg-primary transition-transform duration-150 origin-left"
                        style={{ transform: `scaleX(${Math.max(0.05, scrollProgress)})` }}
                    />
                </div>
                <button
                    onClick={() => scroll("left")}
                    disabled={!canScrollLeft}
                    className="text-primary disabled:text-border transition-colors cursor-pointer disabled:cursor-default"
                    aria-label="Scroll left"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                    onClick={() => scroll("right")}
                    disabled={!canScrollRight}
                    className="text-primary disabled:text-border transition-colors cursor-pointer disabled:cursor-default"
                    aria-label="Scroll right"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>

            {/* Scroll container */}
            <div
                ref={scrollRef}
                className="flex gap-3 overflow-x-auto py-1 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] touch-pan-x"
            >
                {teams.map((team) => (
                    <Link
                        key={team.id}
                        href={team.fansUnitedTeamId ? nav.team(team.fansUnitedTeamId) : "#"}
                        className="shrink-0 w-44 md:w-56 h-56 md:h-56 group/card"
                        prefetch={false}
                    >
                        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-4 md:p-6 transition-shadow hover:shadow-md h-full">
                            <div className="flex items-center justify-center h-20 md:h-24 mb-3">
                                <TeamLogo
                                    src={team.logo}
                                    alt={team.name ?? ""}
                                    size="xl3"
                                    className="max-h-20 md:max-h-24"
                                    primaryColor={team.primaryColor}
                                    secondaryColor={team.secondaryColor}
                                    countryCode={team.countryCode}
                                    countryFlag={team.countryFlag}
                                />
                            </div>
                            <p className="text-xs md:text-sm font-semibold text-card-foreground uppercase tracking-wide text-center leading-tight line-clamp-2">
                                {team.name ?? ""}
                            </p>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

/**
 * TeamLogosScroll - Team logos section.
 *
 * "logos" variant (default):
 *   Mobile: Horizontally scrollable single row.
 *   Desktop: Flex-wrap grid that flows into multiple rows when needed.
 *
 * "cards" variant:
 *   Horizontal scroll of cards with large logos and team names.
 */
export const TeamLogosScroll: React.FC<TeamLogosScrollProps> = ({ teams, variant = "logos", header }) => {
    const nav = useNavigation();

    if (teams.length === 0) return null;

    const sortedTeams = [...teams].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

    if (variant === "cards") {
        return <TeamCardsScroll teams={sortedTeams} nav={nav} header={header} />;
    }

    const TEAMS_PER_ROW = 24;
    const needsTwoRows = sortedTeams.length > TEAMS_PER_ROW;
    const midpoint = needsTwoRows ? Math.ceil(sortedTeams.length / 2) : sortedTeams.length;
    const row1 = sortedTeams.slice(0, midpoint);
    const row2 = needsTwoRows ? sortedTeams.slice(midpoint) : [];

    const renderTeam = (team: Team) => (
        <Link
            key={team.id}
            href={team.fansUnitedTeamId ? nav.team(team.fansUnitedTeamId) : "#"}
            className="shrink-0 hover:opacity-70 transition-opacity"
            prefetch={false}
            title={team.name ?? ""}
        >
            <TeamLogo
                src={team.logo}
                alt={team.name ?? ""}
                size="lg"
                className="max-h-12"
                primaryColor={team.primaryColor}
                secondaryColor={team.secondaryColor}
                countryCode={team.countryCode}
                countryFlag={team.countryFlag}
            />
        </Link>
    );

    return (
        <section>
            {/* Mobile: horizontal scroll (always single row) */}
            <div className="lg:hidden">
                <HorizontalScroll gap="gap-2" showArrows={false}>
                    {sortedTeams.map(renderTeam)}
                </HorizontalScroll>
            </div>

            {/* Desktop: one or two rows, evenly spaced */}
            <div className="hidden lg:flex flex-col gap-3">
                <div className="flex justify-between">{row1.map(renderTeam)}</div>
                {row2.length > 0 && <div className="flex justify-between">{row2.map(renderTeam)}</div>}
            </div>
        </section>
    );
};
